import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useSession } from '@/compositions/hooks/useSession';
import { authService } from '@/services';

import { useAppEntryStore } from '../stores/app-entry.store';

/**
 * Encerra a sessão e devolve à tela inicial (RN-27).
 *
 * ⚠️ A ordem importa: primeiro o serviço de autenticação, depois `clear()` do
 * cache. Limpar antes deixaria uma janela em que a UI já se comporta como
 * deslogada enquanto a sessão ainda existe — e se `signOut` falhar, a pessoa fica
 * numa tela de visitante com sessão viva.
 *
 * `queryClient.clear()` e não `invalidateQueries`: invalidar apenas marca como
 * obsoleto e **mantém os dados em cache** enquanto refaz a busca, então a tela do
 * próximo usuário pisca com o conteúdo do anterior.
 *
 * ## Também é a saída do modo convidado
 *
 * Convidado não tem sessão, então `signOut()` não encerra nada — o que devolve a
 * pessoa à tela inicial é o `reset()` do `app-entry`, que apaga a escolha da
 * RN-01. Chamar o serviço mesmo assim mantém um caminho só: uma segunda mutação
 * "sair de convidado" duplicaria a limpeza de cache e a navegação, e é a cópia que
 * fica para trás quando uma das duas mudar.
 *
 * A mensagem, essa sim, precisa distinguir: anunciar "saiu da sua conta" a quem
 * nunca teve conta descreve algo que não aconteceu.
 *
 * ⚠️ Quem decide a mensagem é a **sessão**, não a flag `isGuest`. Ela sobrevive ao
 * login — quem entrou como convidado e depois criou conta continua marcado como
 * convidado no store —, então usá-la aqui diria "saiu do modo convidado" a alguém
 * que acabou de encerrar uma sessão real.
 */
export function useSignOut() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isAuthenticated } = useSession();
  const resetAppEntry = useAppEntryStore((state) => state.reset);

  return useMutation({
    mutationFn: () => authService.signOut(),

    onSuccess: () => {
      queryClient.clear();
      resetAppEntry();
      toast.success(isAuthenticated ? 'Você saiu da sua conta' : 'Você saiu do modo convidado');
      navigate('/welcome', { replace: true });
    },
  });
}
